'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@ui';
import { IconArrowLeft } from '@icons';
import useBodyScrollLock from './useBodyScrollLock';
import { prefersReducedMotion } from '@utils/prefersReducedMotion';

type Props = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  openBtnRef?: React.RefObject<HTMLButtonElement | null>;
  links?: LinkItem[];
  id?: string;
  /** optional alias for id to make prop semantics clearer when used on the design system page */
  asideID?: string;
  className?: string;
  menuTitle?: string;
  lockBodyScroll?: boolean;
};

type LinkItem = {
  id: string;
  label: string;
  href: string;
  active?: boolean;
};

export const SIDE_LINKS: LinkItem[] = [
  { id: 'dashboard', label: 'Dashboard', href: '#', active: true },
  { id: 'studies', label: 'Studies', href: '#' },
  { id: 'sites', label: 'Sites', href: '#' },
  { id: 'documents', label: 'Documents', href: '#' },
  { id: 'settings', label: 'Settings', href: '#' },
];

const AsideCollapsible: React.FC<Props> = ({
  open: openProp,
  onOpenChange,
  openBtnRef: openBtnRefProp,
  links,
  id: idProp,
  asideID,
  className: classNameProp,
  menuTitle,
  lockBodyScroll = true,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof openProp === 'boolean';
  const open = isControlled ? (openProp as boolean) : internalOpen;

  const sideRef = useRef<HTMLElement | null>(null);
  const internalOpenBtnRef = useRef<HTMLButtonElement | null>(null);
  const openBtnRef = openBtnRefProp ?? internalOpenBtnRef;
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const setOpen = useCallback(
    (v: boolean) => {
      if (onOpenChange) onOpenChange(v);
      if (!isControlled) setInternalOpen(v);
    },
    [onOpenChange, isControlled]
  );

  // Sync aria / class state
  useEffect(() => {
    const el = sideRef.current;
    if (!el) return;
    if (open) {
      el.classList.add('open');
    } else {
      el.classList.remove('open');
    }
  }, [open, openBtnRef, setOpen]);

  // Focus trap and Escape handling
  useEffect(() => {
    if (!open) return;
    const container = sideRef.current;
    if (!container) return;

    lastFocusedRef.current = document.activeElement as HTMLElement | null;
    // Capture the open button at effect start so cleanup uses a stable reference
    const capturedOpenBtn = openBtnRef?.current;
    const focusableSelector =
      'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';
    const nodes = Array.from(container.querySelectorAll(focusableSelector)).filter(
      (n): n is HTMLElement => n instanceof HTMLElement
    );

    // If there are focusable nodes, move focus to the first one
    if (nodes.length) {
      nodes[0].focus();
    } else if (closeBtnRef.current) {
      closeBtnRef.current.focus();
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === 'Tab') {
        // trap
        const focusable = nodes.filter((n) => n.offsetParent !== null);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Restore focus without causing a scroll jump. Prefer the open button
      // (so the user can re-open the aside). Use preventScroll where supported.
      const openBtn = capturedOpenBtn;
      const prev = lastFocusedRef.current;
      try {
        if (openBtn && typeof openBtn.focus === 'function') {
          // preventScroll avoids viewport jumps on focus
          (openBtn as HTMLElement & { focus(opts?: FocusOptions): void }).focus({
            preventScroll: true,
          });
        } else if (prev && typeof prev.focus === 'function') {
          // fallback to previous focused element but still try to prevent scroll
          (prev as HTMLElement & { focus(opts?: FocusOptions): void }).focus({
            preventScroll: true,
          });
        }
      } catch {
        // final fallback: try without options
        try {
          if (openBtn && typeof openBtn.focus === 'function') openBtn.focus();
          else if (prev && typeof prev.focus === 'function') prev.focus();
        } catch {
          /* ignore */
        }
      }
    };
  }, [open, openBtnRef, setOpen]);

  // Outside click closes only on small screens (matchMedia)
  useEffect(() => {
    function handler(e: MouseEvent) {
      const m = window.matchMedia('(max-width: 768px)');
      if (!m.matches) return;
      const target = e.target as Node;
      const container = sideRef.current;
      const openBtn = openBtnRef.current;
      if (!container) return;
      if (container.contains(target)) return;
      if (openBtn && openBtn.contains(target)) return;
      setOpen(false);
    }
    if (open) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open, openBtnRef, setOpen]);

  // Use the reusable hook for the iOS body scroll lock fallback
  useBodyScrollLock(open && lockBodyScroll);

  // Client-only active detection: keep server render identical to provided link.active
  const [mounted, setMounted] = useState(false);
  const [activeIds, setActiveIds] = useState<Set<string>>(() => {
    // Initialize from provided data to match server-rendered HTML
    const initial = new Set<string>();
    (links ?? SIDE_LINKS).forEach((l) => {
      if (l.active) initial.add(l.id);
    });
    return initial;
  });

  useEffect(() => {
    setMounted(true);
    function updateActive() {
      const loc = window.location.hash || window.location.pathname || '';
      const newActive = new Set<string>();
      (links ?? SIDE_LINKS).forEach((link) => {
        const href = link.href || '';
        const isHash = href.startsWith('#');
        const active = isHash ? loc === href : loc === href || loc === href.replace(/#.*$/, '');
        if (active) newActive.add(link.id);
      });
      setActiveIds(newActive);
    }
    updateActive();
    window.addEventListener('hashchange', updateActive);
    window.addEventListener('popstate', updateActive);
    return () => {
      window.removeEventListener('hashchange', updateActive);
      window.removeEventListener('popstate', updateActive);
    };
  }, [links]);

  const handleLinkClick = (link: LinkItem) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    const href = link.href || '';
    if (!href.startsWith('#')) return;
    e.preventDefault();
    const idTarget = href.slice(1);
    const el = document.getElementById(idTarget);
    if (!el) return;

    const isSmall =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 880px)').matches;

    if (isSmall) setOpen(false);

    try {
      if (history && typeof history.pushState === 'function') {
        history.pushState(null, '', `#${idTarget}`);
      } else {
        window.location.hash = `#${idTarget}`;
      }
    } catch {
      // ignore
    }

    setActiveIds(new Set([link.id]));

    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
    const doScroll = () => {
      let headerHeight = 0;
      try {
        const docStyle = getComputedStyle(document.documentElement);
        const v = docStyle.getPropertyValue('--header-height') || '0';
        headerHeight = parseInt(v.replace('px', '').trim(), 10) || 0;
      } catch {
        headerHeight = 0;
      }

      if (!isSmall) {
        const targetTop = el.getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ top: Math.max(0, Math.round(targetTop)), behavior });
        return;
      }

      el.scrollIntoView({ behavior, block: 'start' });
      if (headerHeight > 0) {
        window.scrollBy({ top: -headerHeight, behavior: 'auto' });
      }
    };

    if (isSmall) {
      setTimeout(() => requestAnimationFrame(doScroll), 240);
      setTimeout(() => requestAnimationFrame(doScroll), 520);
    } else {
      doScroll();
    }
  };

  return (
    <aside
      id={asideID ?? idProp ?? 'side'}
      className={`card sidebar${classNameProp ? ' ' + classNameProp : ''}`}
      ref={sideRef}
    >
      <div className='sidebar-head'>
        <b>{menuTitle}</b>
        <Button
          as='button'
          label={<IconArrowLeft />}
          className='btn-round btn-icon'
          ref={closeBtnRef}
          aria-label='Collapse'
          onClick={() => setOpen(false)}
        />
      </div>
      <nav className='sidebar-nav'>
        {/* Render side links from data so this can later be populated from JSON/DB */}
        {(links ?? SIDE_LINKS).map((link) => {
          const isActiveServer = !!link.active;
          const isActiveClient = activeIds.has(link.id);
          // On first render (SSR) we must match server HTML; after mount use client-detected state
          const active = mounted ? isActiveClient : isActiveServer;
          return (
            <a
              key={link.id}
              className={`sidebar-link${active ? ' is-active' : ''}`}
              href={link.href}
              onClick={handleLinkClick(link)}
            >
              {link.label}
            </a>
          );
        })}
      </nav>
    </aside>
  );
};

export default AsideCollapsible;
